import { tr } from '@/i18n';
export enum CustomFieldDataType {
  STRING = "string",
  NUMBER = "number",
  DATE = "date",
  BOOLEAN = "boolean",
  TEXT = "text",
  DROPDOWN_SINGLE = "dropdown_single",
  DROPDOWN_MULTI = "dropdown_multi",
}

export const CUSTOM_FIELD_ENTITY_OPTIONS = [
  { value: "departments", label: tr("role.col_departments", "Departments") },
  { value: "positions", label: tr("role.assign_positions", "Positions") },
  { value: "users", label: tr("ui.user_profile", "User Profile") },
  { value: "courses", label: tr("gradebook.col_course", "Course") },
  { value: "learning_paths", label: tr("ui.program", "Program") },
  { value: "seminar_activity", label: tr("ui.seminar_activity", "Seminar Activity") },
  { value: "facilitator", label: tr("ui.facilitator", "Facilitator") },
  { value: "room", label: tr("ui.room", "Room") },
  { value: "seminar_session", label: tr("ui.seminar_session", "Seminar Session") },
  { value: "course_seminar", label: tr("ui.course_seminar", "Course Seminar") },
  { value: "course_seminar_session", label: tr("ui.course_seminar_session", "Course Seminar Session") },
] as const;

export type CustomFieldEntityType = typeof CUSTOM_FIELD_ENTITY_OPTIONS[number]['value'];
export type CustomFieldConfig = { [key: string]: unknown };

export interface ParsedSelectOption {
  label: string;
  value: string | number;
}

export interface BaseCustomFieldDefinition {
  id: number;
  fullname: string;
  shortname: string;
  description?: string | null;
  required?: boolean;
  config?: Record<string, unknown> | null;
  datatype: CustomFieldDataType | string;
  default_data?: string | null | boolean;
}

export interface CustomFieldDefinition extends BaseCustomFieldDefinition {
  entity_type: CustomFieldEntityType;
  visible: boolean;
  config: CustomFieldConfig | null;
}

export interface BaseCustomFieldValueState<Definition extends BaseCustomFieldDefinition = BaseCustomFieldDefinition> {
  definitionId: number;
  value: unknown;
  definition: Definition;
}

export type CustomFieldFormValues = {
  shortname: string;
  fullname: string;
  description: string;
  datatype: CustomFieldDataType;
  required: boolean;
  visible: boolean;
  default_data: string;
  config: string;
  tenant_ids?: number[];
  /** Already saved on the server — its data type is fixed (framework forms). */
  saved?: boolean;
  /** Saved field's id — sent back so the framework save updates it in place (keeps its values). */
  id?: number;
};

/** API payload shape for creating/updating a custom-field definition (config
 * parsed from the form's JSON string). Formerly lived in competency.types.ts. */
export type CustomFieldDefinitionApiPayload = Omit<CustomFieldFormValues, 'config'> & { config?: Record<string, unknown> | null };

export interface FormDataTypeCustomFieldDefinition {
  shortname: string;
  fullname: string;
  description: string;
  datatype: CustomFieldDataType;
  required: boolean;
  visible: boolean;
  default_data: string;
  config: string;
  tenant_ids?: number[];
}

export type CustomFieldDefinitionCreate = Omit<CustomFieldDefinition, 'id'> & { entity_type: CustomFieldEntityType };
export type CustomFieldDefinitionUpdate = Partial<Omit<CustomFieldDefinition, 'id' | 'entity_type'>>;

export interface PaginatedCustomFieldDefinitionsResponse {
  total: number;
  limit: number;
  offset: number;
  items: CustomFieldDefinition[];
}

export interface CustomFieldDefinitionFilterParams {
  id?: number;
  shortname?: string;
  fullname?: string;
  datatype?: CustomFieldDataType | string;
  required?: boolean;
  visible?: boolean;
  entity_type?: CustomFieldEntityType;
  tenant_id?: number | null;
}

export interface CustomFieldFormProps {
  initialValues?: Partial<CustomFieldFormValues>;
  onSubmit: (values: CustomFieldFormValues) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  entityTypeLabel?: string;
  isEditing?: boolean;
}

export interface CustomFieldsProps {
  initialValues?: Partial<CustomFieldFormValues>;
  onSubmit: (values: CustomFieldFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  entityTypeLabel?: string;
  isEditing?: boolean;
  renderFormTag?: boolean;
  onDataChange?: (values: CustomFieldFormValues) => void;
}

export interface CustomFieldInputRendererProps {
  fieldState: BaseCustomFieldValueState<BaseCustomFieldDefinition>;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

export interface UseManageableCustomFieldsOptions<Definition extends BaseCustomFieldDefinition> {
  availableDefinitions: Definition[];
  initialValues?: BaseCustomFieldValueState<Definition>[];
}

export interface UseManageableCustomFieldsReturn<Definition extends BaseCustomFieldDefinition> {
  selectedCustomFields: BaseCustomFieldValueState<Definition>[];
  setSelectedCustomFields: React.Dispatch<React.SetStateAction<BaseCustomFieldValueState<Definition>[]>>;
  addCustomField: (definitionId: number) => void;
  removeCustomField: (indexOrDefinitionId: number) => void;
  updateCustomFieldValue: (indexOrDefinitionId: number, value: unknown) => void;
  unselectedDefinitions: Definition[];
  getCustomFieldPayload: () => Array<{ field_definition_id: number; value: unknown }>;
  validateCustomFields: () => { isValid: boolean; errorField?: string | null };
}

export interface CustomFieldDefinitionFormOptions {
  entityType: CustomFieldEntityType;
  onSuccess?: (definition: CustomFieldDefinition) => void;
  definitionToEdit?: CustomFieldDefinition | null;
}

export interface CustomFieldDefinitionFormProps {
  onSuccess: (definition: CustomFieldDefinition) => void;
  definitionToEdit: CustomFieldDefinition | null;
  setShowForm: (show: boolean) => void;
  entityType: CustomFieldEntityType;
  entityTypeLabel: string;
}

export interface CustomFieldDefinitionsManagementProps {
  entityType: CustomFieldEntityType;
}